import { useState, useEffect } from 'react';
import React from 'react';

import { useNavigate } from 'react-router-dom';
import { useViewAs } from 'hooks/useViewAs';

import { useQuery } from '@tanstack/react-query';

import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Td,
  Text,
  Th,
  Tr,
  Select,
  Input,
  IconButton,
  Tooltip,
  Button,
  Collapse,
  HStack,
  Stack,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Switch,
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  Portal,
  useColorMode,
  useToken,
  Spinner,
  Badge,
} from '@chakra-ui/react';

import {
  CalendarIcon,
  SearchIcon,
  SmallCloseIcon,
  ChevronUpIcon,
  ChevronDownIcon,
} from '@chakra-ui/icons';

import Paginate from 'components/paginate/Paginate';

// @ts-ignore
import { DateRangePicker } from 'react-date-range';

import Card from 'components/card/Card';

import { apiGetBinderList } from 'api';
import { AuthContext } from 'contexts/AuthContext';
import { useContext } from 'react';

import getDateString from 'helpers/getDateString';

import type { APIPaginatedResponse, APIBinderItem } from 'types/api.types';
import { ROLE_ADMIN, ROLE_MANAGER } from 'variables/constants';

import { useUrlFilters } from 'hooks/useUrlFilters';
import { useFeatureFlag } from 'hooks/useFeatureFlag';

const BINDER_FILTER_KEYS = [
  'start_date',
  'end_date',
  'wip_status',
  'work_type',
  'submission_type',
  'carrier',
  'client_name',
  'sort_field',
  'sort_direction',
] as const;

const BINDER_DEFAULTS: Record<string, string> = {
  sort_field: 'created_at',
  sort_direction: 'desc',
};

const ValidatedPoliciesView = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const {
    getFilter,
    setFilter,
    setFilters,
    setFiltersAndPage,
    page,
    setPage,
    resetAll,
    searchParams,
  } = useUrlFilters(BINDER_FILTER_KEYS, BINDER_DEFAULTS);

  const [periodDates, setPeriodDates] = useState({
    startDate: null as Date | null,
    endDate: null as Date | null,
    key: 'selection',
  });
  const [, setAnalyticsKey] = useState('initial');
  const [mpViewEnabled, setMpViewEnabled] = useState(false);
  const [showSearchBar, setShowSearchBar] = useState(false);
  const [carrierInputValue, setCarrierInputValue] = useState('');
  const [clientNameInputValue, setClientNameInputValue] = useState('');

  const statusFilter = getFilter('wip_status');
  const workTypeFilter = getFilter('work_type');
  const submissionTypeFilter = getFilter('submission_type');
  const carrierFilter = getFilter('carrier');
  const clientNameFilter = getFilter('client_name');
  const sortField = getFilter('sort_field') || 'created_at';
  const sortDirection = (
    getFilter('sort_direction') === 'asc' ? 'asc' : 'desc'
  ) as 'asc' | 'desc';

  useEffect(() => {
    const startStr = searchParams.get('start_date');
    const endStr = searchParams.get('end_date');
    setPeriodDates({
      startDate: startStr ? new Date(startStr) : null,
      endDate: endStr ? new Date(endStr) : null,
      key: 'selection',
    });
    if (startStr && endStr) setAnalyticsKey(`${startStr}-${endStr}`);
    setCarrierInputValue(searchParams.get('carrier') ?? '');
    setClientNameInputValue(searchParams.get('client_name') ?? '');
  }, [searchParams]);

  const viewAs = useViewAs();
  const isViewAsEnabled = useFeatureFlag('enable-desktop-view-as');
  const isJiraWipStatusesEnabled = useFeatureFlag(
    'enable-jira-submission-wip-statuses',
  );

  const filters = React.useMemo(() => {
    const result: any = {
      start_date: getDateString(periodDates.startDate),
      end_date: getDateString(periodDates.endDate),
    };
    if (statusFilter) result.wip_status = parseInt(statusFilter);
    if (workTypeFilter) result.work_type = parseInt(workTypeFilter);
    if (submissionTypeFilter)
      result.submission_type = parseInt(submissionTypeFilter);
    if (carrierFilter.trim()) result.carrier = carrierFilter.trim();
    if (clientNameFilter.trim()) result.client_name = clientNameFilter.trim();
    if (sortField) result.sort_field = sortField;
    if (sortDirection) result.sort_direction = sortDirection;
    return { ...result, ...viewAs.filterParams };
  }, [
    statusFilter,
    workTypeFilter,
    submissionTypeFilter,
    carrierFilter,
    clientNameFilter,
    sortField,
    sortDirection,
    periodDates,
    viewAs.filterParams,
  ]);

  const handleClientNameInputChange = (value: string) => {
    setClientNameInputValue(value);
  };

  const handleSearch = () => {
    setFilters({
      carrier: carrierInputValue.trim(),
      client_name: clientNameInputValue.trim(),
    });
  };

  const resetSearch = () => {
    setCarrierInputValue('');
    setClientNameInputValue('');
    setPeriodDates({
      startDate: null,
      endDate: null,
      key: 'selection',
    });
    setAnalyticsKey('initial');
    resetAll();
  };

  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setFiltersAndPage(
        { sort_direction: sortDirection === 'asc' ? 'desc' : 'asc' },
        0,
      );
    } else {
      setFiltersAndPage({ sort_field: field, sort_direction: 'asc' }, 0);
    }
  };

  const { colorMode } = useColorMode();
  const [brand500] = useToken('colors', ['brand.500']);

  const colors = {
    textColor: colorMode === 'light' ? 'secondaryGray.900' : 'white',
    borderColor: colorMode === 'light' ? 'gray.200' : 'whiteAlpha.100',
    hoverBg: colorMode === 'light' ? 'gray.200' : 'whiteAlpha.100',
  } as const;

  const textColor = colors.textColor;
  const borderColor = colors.borderColor;
  const hoverBg = colors.hoverBg;

  useEffect(() => {
    window.scrollTo({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }, []);

  const { error, data, isLoading } = useQuery({
    queryKey: ['binder-list', page, filters],
    queryFn: () => apiGetBinderList(page + 1, filters),
  }) as {
    data: APIPaginatedResponse<APIBinderItem>;
    isLoading: boolean;
    error: any;
    isFetching: boolean;
  };

  const onBinderClick = (binder: APIBinderItem) => {
    navigate(`/admin/policies/overview/${binder.uuid}`);
  };

  const canSeeBrokerName =
    user.role === ROLE_ADMIN || (user.role === ROLE_MANAGER && mpViewEnabled);

  const getStatusBadge = (status: string) => {
    const statusConfig: Record<string, { color: string; bg: string }> = {
      'Pending Upload': { color: '#27273e', bg: '#fce8b0' },
      Uploaded: { color: '#559e67', bg: '#e3fde2' },
      Approved: { color: '#1a5c2a', bg: '#c6f6d5' },
      Declined: { color: '#822727', bg: '#fed7d7' },
      'UW Declined': { color: '#822727', bg: '#fed7d7' },
    };

    const config = statusConfig[status] || {
      color: 'gray.500',
      bg: 'gray.100',
    };

    return (
      <Box
        bg={config.bg}
        color={config.color}
        px={2}
        py={1}
        borderRadius="md"
        fontSize="xs"
        fontWeight="bold"
        textAlign="center"
        minW="80px"
      >
        {status}
      </Box>
    );
  };

  const onDateChangeConfirm = (rest: boolean) => {
    if (rest) {
      setPeriodDates({
        startDate: null,
        endDate: null,
        key: 'selection',
      });
      setAnalyticsKey('initial');
      setFilters({
        start_date: '',
        end_date: '',
      });
    } else {
      const startStr = getDateString(periodDates.startDate);
      const endStr = getDateString(periodDates.endDate);
      setAnalyticsKey(startStr && endStr ? `${startStr}-${endStr}` : 'initial');
      setFilters({
        start_date: startStr || '',
        end_date: endStr || '',
      });
    }
  };

  // if (isLoading) {
  //   return <Card textAlign="center">Loading binders...</Card>;
  // }

  if (error) {
    return (
      <Card textAlign="center">
        <Alert status="error">
          <AlertIcon />
          <AlertTitle>Error</AlertTitle>
          <AlertDescription>{JSON.stringify(error)}</AlertDescription>
        </Alert>
      </Card>
    );
  }

  return (
    <Card
      flexDirection="column"
      w="100%"
      px="0px"
      overflowX={{ sm: 'scroll', lg: 'hidden' }}
    >
      <Flex px="20px" justifyContent="space-between" align="center" mb="20px">
        <Text
          color={textColor}
          fontSize="22px"
          fontWeight="700"
          lineHeight="100%"
        >
          Binders
        </Text>
        <HStack spacing={4}>
          {user.role === ROLE_MANAGER && (
            <HStack spacing={2}>
              <Text fontSize="sm" color={textColor}>
                MP View
              </Text>
              <Switch
                colorScheme="brand"
                size="md"
                isChecked={mpViewEnabled}
                onChange={(e) => setMpViewEnabled(e.target.checked)}
              />
            </HStack>
          )}
          <Tooltip
            label={showSearchBar ? 'Hide search' : 'Show search'}
            hasArrow
          >
            <IconButton
              aria-label={showSearchBar ? 'Hide search' : 'Show search'}
              icon={<SearchIcon />}
              variant={showSearchBar ? 'solid' : 'ghost'}
              colorScheme={showSearchBar ? 'yellow' : 'gray'}
              onClick={() => setShowSearchBar(!showSearchBar)}
              size="md"
            />
          </Tooltip>
        </HStack>
      </Flex>

      <Collapse in={showSearchBar} animateOpacity>
        <Box px="20px" mb="20px">
          <Stack
            spacing={4}
            direction={{ base: 'column', lg: 'row' }}
            align="flex-end"
          >
            <Box flex="1">
              <Select
                variant="main"
                placeholder="All Statuses"
                value={statusFilter}
                onChange={(e) => setFilter('wip_status', e.target.value)}
                size="md"
                onKeyDown={handleKeyDown}
              >
                <option value="11">Pending Upload</option>
                <option value="12">Uploaded</option>
                {isJiraWipStatusesEnabled && (
                  <>
                    <option value="19">Declined</option>
                    <option value="18">UW Declined</option>
                  </>
                )}
              </Select>
            </Box>
            <Box flex="1">
              <Input
                variant="main"
                placeholder="Search by client name"
                value={clientNameInputValue}
                onChange={(e) => handleClientNameInputChange(e.target.value)}
                size="md"
                onKeyDown={handleKeyDown}
              />
            </Box>

            <Box>
              <Popover
                placement="bottom-start"
                closeOnBlur={true}
                strategy="fixed"
                isLazy
              >
                <PopoverTrigger>
                  <IconButton
                    aria-label="Filter by effective date"
                    icon={<CalendarIcon />}
                    variant={
                      periodDates.startDate || periodDates.endDate
                        ? 'solid'
                        : 'ghost'
                    }
                    colorScheme={
                      periodDates.startDate || periodDates.endDate
                        ? 'blue'
                        : 'gray'
                    }
                    size="md"
                  />
                </PopoverTrigger>
                <Portal>
                  <PopoverContent w="595px" mt="4" zIndex={1400}>
                    <PopoverBody>
                      <DateRangePicker
                        onChange={(item: any) => setPeriodDates(item.selection)}
                        showSelectionPreview={true}
                        moveRangeOnFirstSelection={false}
                        ranges={[periodDates]}
                        firstDayOfWeek={1}
                        direction="horizontal"
                        scroll={{ enabled: true }}
                        className={`date-range-picker ${colorMode}`}
                        rangeColors={[brand500]}
                      />
                      <Flex gap={2} mt="3">
                        <Button
                          onClick={() => onDateChangeConfirm(false)}
                          variant="brand"
                          size="sm"
                        >
                          Apply Date Filter
                        </Button>
                        <Button
                          onClick={() => onDateChangeConfirm(true)}
                          variant="outline"
                          size="sm"
                        >
                          Reset Dates
                        </Button>
                      </Flex>
                    </PopoverBody>
                  </PopoverContent>
                </Portal>
              </Popover>
            </Box>

            <Flex gap={2}>
              <Button
                onClick={handleSearch}
                leftIcon={<SearchIcon />}
                size="md"
                variant="brand"
                isLoading={isLoading}
              >
                Search
              </Button>
              <Button
                onClick={resetSearch}
                variant="outline"
                leftIcon={<SmallCloseIcon />}
                size="md"
              >
                Reset
              </Button>
            </Flex>
          </Stack>
        </Box>
      </Collapse>

      <Box overflowX="auto">
        <Table variant="simple" color="gray.500">
          <Thead>
            <Tr>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Client Name</Text>
                  <Tooltip label="Sort by Client Name">
                    <IconButton
                      aria-label="Sort by Client Name"
                      icon={
                        sortField === 'client_name' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('client_name')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Binder Number</Text>
                  <Tooltip label="Sort by Binder Number">
                    <IconButton
                      aria-label="Sort by Binder Number"
                      icon={
                        sortField === 'binder_number' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('binder_number')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Submit Date</Text>
                  <Tooltip label="Sort by Submit Date">
                    <IconButton
                      aria-label="Sort by Submit Date"
                      icon={
                        sortField === 'created_at' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('created_at')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Effective Date</Text>
                  <Tooltip label="Sort by Effective Date">
                    <IconButton
                      aria-label="Sort by Effective Date"
                      icon={
                        sortField === 'effective_date' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('effective_date')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Submission Type</Text>
                  <Tooltip label="Sort by Submission Type">
                    <IconButton
                      aria-label="Sort by Submission Type"
                      icon={
                        sortField === 'submission_type' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('submission_type')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Work Type</Text>
                  <Tooltip label="Sort by Work Type">
                    <IconButton
                      aria-label="Sort by Work Type"
                      icon={
                        sortField === 'work_type_display' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('work_type_display')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Premium Amount</Text>
                  <Tooltip label="Sort by Premium Amount">
                    <IconButton
                      aria-label="Sort by Premium Amount"
                      icon={
                        sortField === 'premium' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('premium')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
              {canSeeBrokerName && (
                <Th borderColor={borderColor} color="gray.400">
                  <HStack spacing={1}>
                    <Text color="gray.400">Broker Name</Text>
                    <Tooltip label="Sort by Broker Name">
                      <IconButton
                        aria-label="Sort by Broker Name"
                        icon={
                          sortField === 'broker_name' ? (
                            sortDirection === 'asc' ? (
                              <ChevronUpIcon />
                            ) : (
                              <ChevronDownIcon />
                            )
                          ) : (
                            <ChevronUpIcon />
                          )
                        }
                        size="xs"
                        variant="ghost"
                        onClick={() => handleSort('broker_name')}
                      />
                    </Tooltip>
                  </HStack>
                </Th>
              )}
              <Th borderColor={borderColor} color="gray.400">
                <HStack spacing={1}>
                  <Text color="gray.400">Status</Text>
                  <Tooltip label="Sort by Status">
                    <IconButton
                      aria-label="Sort by Status"
                      icon={
                        sortField === 'wip_status_display' ? (
                          sortDirection === 'asc' ? (
                            <ChevronUpIcon />
                          ) : (
                            <ChevronDownIcon />
                          )
                        ) : (
                          <ChevronUpIcon />
                        )
                      }
                      size="xs"
                      variant="ghost"
                      onClick={() => handleSort('wip_status_display')}
                    />
                  </Tooltip>
                </HStack>
              </Th>
            </Tr>
          </Thead>
          <Tbody>
            {isLoading && (
              <Tr>
                <Td colSpan={9} textAlign="center">
                  <Flex align="center" justify="center" mt="20px">
                    <Spinner size="sm" mr="10px" /> Loading policies...
                  </Flex>
                </Td>
              </Tr>
            )}

            {data &&
              data.results.map((binder) => (
                <Tr
                  key={binder.uuid}
                  onClick={() => onBinderClick(binder)}
                  cursor="pointer"
                  _hover={{
                    bg: hoverBg,
                  }}
                  bg={
                    isViewAsEnabled && binder.is_new
                      ? 'blue.50'
                      : isViewAsEnabled && binder.is_stale
                        ? 'orange.50'
                        : undefined
                  }
                  _dark={{
                    bg:
                      isViewAsEnabled && binder.is_new
                        ? 'blue.900'
                        : isViewAsEnabled && binder.is_stale
                          ? 'orange.900'
                          : undefined,
                  }}
                >
                  <Td>
                    <Flex align="center">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {binder.client_name || '-'}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align="center">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {binder.binder_number || '-'}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align="center">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {binder.uploaded_date_formatted || '-'}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align="center">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {binder.effective_date_formatted || '-'}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align="center">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {binder.submission_type === 'Life' &&
                        binder.life_product
                          ? `Life/${binder.life_product}`
                          : binder.submission_type || '-'}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align="center">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {binder.work_type_display || '-'}
                      </Text>
                    </Flex>
                  </Td>
                  <Td>
                    <Flex align="center">
                      <Text color={textColor} fontSize="sm" fontWeight="700">
                        {binder.premium
                          ? `$${binder.premium.toLocaleString()}`
                          : '-'}
                      </Text>
                    </Flex>
                  </Td>
                  {canSeeBrokerName && (
                    <Td>
                      <Flex align="center">
                        <Text color={textColor} fontSize="sm" fontWeight="700">
                          {binder.broker_name || '-'}
                        </Text>
                      </Flex>
                    </Td>
                  )}
                  <Td>
                    <Flex align="center" gap={2}>
                      {getStatusBadge(binder.wip_status_display)}
                      {isViewAsEnabled && binder.is_new && (
                        <Badge colorScheme="blue" fontSize="9px">
                          NEW
                        </Badge>
                      )}
                      {isViewAsEnabled && binder.is_stale && !binder.is_new && (
                        <Badge colorScheme="orange" fontSize="9px">
                          OVERDUE
                        </Badge>
                      )}
                    </Flex>
                  </Td>
                </Tr>
              ))}
          </Tbody>
        </Table>
      </Box>

      {data && data.results.length === 0 && (
        <Box textAlign="center" w="100%" mt="20px">
          No binders found
        </Box>
      )}

      <Paginate page={page} count={data?.count || 0} onSetPage={setPage} />
    </Card>
  );
};

export default ValidatedPoliciesView;
